import { apiFetch } from "./client";
import { getAuthCredentials } from "@/store/useAuthStore";
import { viewAsHeaderValue } from "@/lib/viewAs";

/**
 * D-146 W2 «Матеріали» — API module for the Client360 materials tab.
 *
 * Everything is backend-scoped: canCreate / canCreateReason / allowedActions /
 * counts come from the server and are NEVER derived on the FE (TZ §3.2).
 * Download/preview go through the document-id-first hardened endpoints with
 * blob + header auth (D-131) — never a bare <a href> (401 in this deployment).
 */

const API_BASE = "/api/v1";

export type MaterialCategory = "kp" | "matrix" | "marketing" | "other";
export type MaterialSort = "newest" | "oldest" | "name";

export const MATERIAL_CATEGORY_LABEL: Record<MaterialCategory, string> = {
  kp: "КП",
  matrix: "Матриця",
  marketing: "Маркетингові матеріали",
  other: "Інше",
};

export const MATERIAL_SORT_LABEL: Record<MaterialSort, string> = {
  newest: "Спочатку нові",
  oldest: "Спочатку старі",
  name: "За назвою",
};

export interface MaterialAllowedActions {
  view: boolean;
  download: boolean;
  editMeta: boolean;
  delete: boolean;
}

export interface ClientMaterial {
  id: string;
  name: string;
  /** Immutable original file name (tooltip/subtitle + search axis #2). */
  originalFilename: string;
  category: MaterialCategory;
  extension: string;
  fileSize: number | null;
  mimeType: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  allowedActions: MaterialAllowedActions;
}

export interface ClientMaterialsMeta {
  canCreate: boolean;
  canCreateReason: "forbidden" | "limit_reached" | null;
  activeCount: number;
  maxCount: number;
  remainingSlots: number;
  categoryCounts: Record<MaterialCategory, number>;
}

export interface ClientMaterialsListResponse {
  items: ClientMaterial[];
  filteredTotal: number;
  meta: ClientMaterialsMeta;
}

export interface MaterialsFilters {
  search?: string;
  category?: MaterialCategory | "";
  sort?: MaterialSort;
}

export function fetchClientMaterials(
  holdingId: string,
  filters: MaterialsFilters,
  signal?: AbortSignal,
): Promise<ClientMaterialsListResponse> {
  return apiFetch(`/Custom/holdings/${encodeURIComponent(holdingId)}/materials`, {
    query: {
      search: filters.search || undefined,
      category: filters.category || undefined,
      sort: filters.sort || undefined,
    },
    signal,
  });
}

export function fetchClientMaterial(id: string): Promise<ClientMaterial> {
  return apiFetch(`/Custom/client-materials/${encodeURIComponent(id)}`);
}

export function updateClientMaterialMeta(
  id: string,
  body: { name?: string; category?: MaterialCategory; confirmDuplicate?: boolean },
): Promise<{ item: ClientMaterial }> {
  return apiFetch(`/Custom/client-materials/${encodeURIComponent(id)}/meta`, {
    method: "PATCH",
    body,
  });
}

export function deleteClientMaterial(
  id: string,
): Promise<{ deleted: boolean; meta: ClientMaterialsMeta }> {
  return apiFetch(`/Custom/client-materials/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export type MaterialRequestStatus =
  | { status: "absent" }
  | { status: "pending" }
  | { status: "failed"; errorCode?: string }
  | { status: "committed"; documentId: string; item?: ClientMaterial };

export function fetchMaterialRequestStatus(
  holdingId: string,
  clientRequestId: string,
): Promise<MaterialRequestStatus> {
  return apiFetch(
    `/Custom/holdings/${encodeURIComponent(holdingId)}/material-requests/${encodeURIComponent(clientRequestId)}`,
  );
}

/** Hardened document-id-first streaming URLs (blob + header auth, D-131). */
export function materialDownloadUrl(id: string): string {
  return `${API_BASE}/Custom/client-materials/${encodeURIComponent(id)}/download`;
}

export function materialViewUrl(id: string): string {
  return `${API_BASE}/Custom/client-materials/${encodeURIComponent(id)}/view`;
}

// ── Upload transport (TZ §9) ────────────────────────────────────────────────
//
// STEP-0: the existing fetch/base64 POST /Attachment transport has NO honest
// byte progress and no abort — so uploads go through XMLHttpRequest with
// multipart FormData to the custom endpoint: upload.onprogress = real
// transport bytes, xhr.abort() = real cancel. The clientRequestId idempotency
// key + the request-status endpoint reconcile the abort/commit race.

export interface MaterialCreateResult {
  documentId: string;
  item: ClientMaterial;
  meta: ClientMaterialsMeta;
  idempotentReplay?: boolean;
}

export interface DuplicateExisting {
  id: string;
  name: string;
  originalFilename: string;
  createdAt: string;
  createdBy: string;
}

export class MaterialUploadError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Filled for 409 duplicate_material. */
    public readonly existing?: DuplicateExisting,
  ) {
    super(message);
    this.name = "MaterialUploadError";
  }
}

/** Thrown when the client aborted the transport (reconcile by request status). */
export class MaterialUploadAborted extends Error {
  constructor() {
    super("aborted");
    this.name = "MaterialUploadAborted";
  }
}

export interface MaterialUploadHandle {
  promise: Promise<MaterialCreateResult>;
  abort: () => void;
}

export function uploadClientMaterial(
  holdingId: string,
  file: File,
  fields: {
    name: string;
    category: MaterialCategory;
    clientRequestId: string;
    confirmDuplicate?: boolean;
  },
  onProgress?: (loadedBytes: number, totalBytes: number) => void,
): MaterialUploadHandle {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<MaterialCreateResult>((resolve, reject) => {
    const url = new URL(
      `${API_BASE}/Custom/holdings/${encodeURIComponent(holdingId)}/materials`,
      window.location.origin,
    );
    xhr.open("POST", url.toString());
    xhr.responseType = "text";

    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    const creds = getAuthCredentials();
    if (creds) {
      const enc = btoa(unescape(encodeURIComponent(`${creds.userName}:${creds.token}`)));
      xhr.setRequestHeader("Espo-Authorization", enc);
      xhr.setRequestHeader("Authorization", `Basic ${enc}`);
      xhr.setRequestHeader("Espo-Authorization-By-Token", "true");
    }
    const viewAs = viewAsHeaderValue();
    if (viewAs) xhr.setRequestHeader("X-Kormax-View-As", viewAs);
    // F0A CSRF guard: mutating verbs require the X-XSRF-Token echo (mirror of apiFetch).
    const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
    if (xsrf) xhr.setRequestHeader("X-XSRF-Token", decodeURIComponent(xsrf[1]));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body) {
        resolve(body as MaterialCreateResult);
        return;
      }
      const flat = (body ?? {}) as {
        code?: string;
        message?: string;
        existing?: DuplicateExisting;
      };
      const statusReason = xhr.getResponseHeader("X-Status-Reason") ?? "";
      reject(
        new MaterialUploadError(
          xhr.status,
          flat.code ?? `http_${xhr.status}`,
          flat.message || statusReason || "Не вдалося завантажити файл.",
          flat.existing,
        ),
      );
    };
    xhr.onerror = () =>
      reject(new MaterialUploadError(0, "network_error", "Помилка мережі. Спробуйте ще раз."));
    xhr.onabort = () => reject(new MaterialUploadAborted());

    const form = new FormData();
    form.append("file", file, file.name);
    form.append("name", fields.name);
    form.append("category", fields.category);
    form.append("clientRequestId", fields.clientRequestId);
    if (fields.confirmDuplicate) form.append("confirmDuplicate", "true");
    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}

/** Client-side pre-checks mirror the server allowlist (server stays decisive). */
export const MATERIAL_ACCEPT =
  ".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

export const MATERIAL_MAX_BYTES = 20 * 1024 * 1024;

export const MATERIAL_ALLOWED_EXTENSIONS = ["pdf", "docx", "xlsx", "pptx", "jpg", "jpeg", "png"];

export function newClientRequestId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "");
  } catch {
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  }
}

export function formatFileSize(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1048576).toFixed(1).replace(".", ",")} МБ`;
}
